import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { mockComments, type CommentItem } from '@/mocks/comments';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/d90jls2181e9e92u94eg';

export default function CommentSection() {
  const { id } = useParams<{ id: string }>();
  const [comments, setComments] = useState<CommentItem[]>(
    // deepest first
    [...mockComments.filter((c) => c.articleId === id)].reverse(),
  );
  const [author, setAuthor] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [charsLeft, setCharsLeft] = useState(500);

  const commentCount = comments.length;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFeedback({ type: 'success', msg: '评论已提交，等待审核后显示~' });
      setTimeout(() => setFeedback(null), 4000);
      return;
    }

    if (!author.trim() || !commentText.trim()) {
      setFeedback({ type: 'error', msg: '昵称和评论内容不能为空哦' });
      return;
    }

    if (commentText.length > 500) {
      setFeedback({ type: 'error', msg: '评论内容超过500字限制' });
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', author.trim());
      body.append('comment', commentText.trim());
      body.append('article_id', id || '');

      const resp = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (resp.ok) {
        const newComment: CommentItem = {
          id: `local-${Date.now()}`,
          articleId: id || '',
          author: author.trim(),
          content: commentText.trim(),
          date: new Date().toLocaleString('zh-CN', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
          }),
          likes: 0,
        };
        setComments((prev) => [newComment, ...prev]);
        setAuthor('');
        setCommentText('');
        setCharsLeft(500);
        setFeedback({ type: 'success', msg: '评论发布成功！' });
      } else {
        setFeedback({ type: 'error', msg: '评论提交失败，请稍后重试' });
      }
    } catch {
      setFeedback({ type: 'error', msg: '网络错误，请检查网络后重试' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const visibleComments = useMemo(() => comments, [comments]);

  if (!id) return null;

  return (
    <section className="py-14 md:py-18 bg-background-50">
      <div className="w-full max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            评论区
            {commentCount > 0 && (
              <span className="ml-3 text-base font-normal text-foreground-400">
                ({commentCount})
              </span>
            )}
          </h2>
          <div className="flex items-center gap-1.5 text-foreground-400">
            <i className="ri-message-3-line text-sm"></i>
            <span className="text-xs">吃瓜群众聚集地</span>
          </div>
        </div>

        {/* Comment Form */}
        <form
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-background-100/60 border border-foreground-200/15 rounded-2xl p-5 md:p-6 mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/30 to-accent-500/30 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-foreground-500 text-lg"></i>
            </div>
            <div>
              <p className="text-foreground-800 text-sm font-medium">发表你的看法</p>
              <p className="text-foreground-400 text-xs">文明吃瓜，理性发言</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            <input
              type="text"
              name="name"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="你的昵称"
              maxLength={20}
              className="flex-1 px-3.5 py-2.5 text-sm rounded-lg bg-background-50 border border-foreground-200/20 text-foreground-800 placeholder:text-foreground-400/60 focus:outline-none focus:border-primary-500/50 transition-colors"
            />
          </div>

          <div className="relative mb-3">
            <textarea
              name="comment"
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                setCharsLeft(500 - e.target.value.length);
              }}
              placeholder="写下你的看法，和吃瓜群众一起讨论..."
              rows={3}
              maxLength={500}
              className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-background-50 border border-foreground-200/20 text-foreground-800 placeholder:text-foreground-400/60 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
            ></textarea>
            <span className={`absolute bottom-2.5 right-3 text-xs ${charsLeft <= 50 ? 'text-primary-500' : 'text-foreground-400'}`}>
              {charsLeft}
            </span>
          </div>

          {/* Honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="form-honeypot"
          />

          <div className="flex items-center justify-between">
            <span className="text-foreground-400 text-xs">
              <i className="ri-information-line mr-1"></i>
              评论将在审核后显示
            </span>
            <button
              type="submit"
              disabled={submitting || !author.trim() || !commentText.trim()}
              className="whitespace-nowrap px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>
                  发布中
                </span>
              ) : (
                '发布评论'
              )}
            </button>
          </div>

          {feedback && (
            <div className={`mt-3 px-4 py-2.5 rounded-lg text-sm text-center ${
              feedback.type === 'success'
                ? 'bg-primary-500/10 text-primary-600 border border-primary-500/20'
                : 'bg-red-500/10 text-red-500 border border-red-500/20'
            }`}>
              {feedback.msg}
            </div>
          )}
        </form>

        {/* Comment List */}
        {visibleComments.length > 0 ? (
          <div className="space-y-4">
            {visibleComments.map((comment) => (
              <CommentCard key={comment.id} comment={comment} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100/60 flex items-center justify-center">
              <i className="ri-chat-3-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">还没有评论，来当第一个吃瓜群众吧！</p>
          </div>
        )}
      </div>
    </section>
  );
}

function CommentCard({ comment }: { comment: CommentItem }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.likes);

  const handleLike = () => {
    if (liked) {
      setLikeCount((c) => c - 1);
    } else {
      setLikeCount((c) => c + 1);
    }
    setLiked(!liked);
  };

  const isLocal = comment.id.startsWith('local-');

  return (
    <div className="bg-background-100/40 border border-foreground-200/10 rounded-xl p-4 md:p-5 transition-colors hover:border-foreground-200/20">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-secondary-500/30 to-accent-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-foreground-600 text-xs font-bold">
            {comment.author.charAt(0)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-foreground-800 text-sm font-semibold">{comment.author}</span>
            {isLocal && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary-500/10 text-primary-500 font-medium">
                刚刚
              </span>
            )}
            <span className="text-foreground-400 text-xs">{comment.date}</span>
          </div>
          <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-wrap">
            {comment.content}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 text-xs cursor-pointer transition-colors ${
                liked ? 'text-primary-500' : 'text-foreground-400 hover:text-primary-400'
              }`}
            >
              <i className={`${liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`}></i>
              <span>{likeCount > 0 ? likeCount : '赞'}</span>
            </button>
            <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
              <i className="ri-chat-1-line text-sm"></i>
              <span>回复</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}